import React, { memo } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { XIcon } from '@heroicons/react/solid';
import { useEmbeddedReportUrl } from '../../../../hooks/useEmbeddedReportUrl';

// Loading spinner component
const LoadingSpinner = () => (
  <svg
    className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export const DocumentEmbedDialog = memo(function DocumentEmbedDialog({ 
  isOpen, 
  onClose, 
  reportStage, 
  clinicianId, 
  patientId, 
  lastGenerated,
  displayTitle
}) {
  const { url, isLoading, error, fetch, refresh } = useEmbeddedReportUrl(
    patientId,
    reportStage,
    lastGenerated,
    clinicianId
  );

  React.useEffect(() => {
    if (isOpen && reportStage) {
      fetch().catch(() => {});
    }
  }, [isOpen, reportStage, fetch]);

  // Chrome (dialog heading + iframe title) shows the user-facing name when a
  // caller supplies one (e.g. Autism passes "MDT Review"); otherwise it falls
  // back to the internal reportStage so ADHD callers are unchanged. We always
  // suffix " Report" for a consistent heading, but skip it when the chosen
  // title already ends in "Report" (e.g. "Autism Patient Report") to avoid a
  // doubled "... Report Report".
  const titleBase = displayTitle || reportStage;
  const dialogTitle = /report$/i.test(titleBase) ? titleBase : `${titleBase} Report`;

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 z-[1000]" />
        <Dialog.Content className="radix-dialog-content-centered-motion fixed left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 
                                   w-[90vw] max-w-5xl h-[80vh] bg-white rounded-lg 
                                   shadow-lg p-4 focus:outline-none z-[1001]">
          <div className="flex flex-col h-full">
            <div className="flex justify-between items-center mb-4">
              <Dialog.Title className="text-xl font-semibold">
                {dialogTitle}
              </Dialog.Title>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1 text-sm font-semibold rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                  onClick={() => { if (url) window.open(url, '_blank'); }}
                  disabled={!url}
                >
                  Open in new tab
                </button>
                <button
                  className="px-3 py-1 text-sm font-semibold rounded border border-gray-300 hover:bg-gray-50"
                  onClick={() => refresh().catch(() => {})}
                >
                  Refresh
                </button>
                <Dialog.Close asChild>
                  <button 
                    className="rounded-full p-1 hover:bg-gray-200 focus:outline-none" 
                    aria-label="Close"
                  >
                    <XIcon className="h-5 w-5" />
                  </button>
                </Dialog.Close>
              </div>
            </div>
            
            <div className="flex-1 overflow-hidden rounded-md border border-gray-300">
              {isLoading ? (
                <div className="h-full flex items-center justify-center">
                  <LoadingSpinner />
                  <span className="ml-2">Loading document...</span>
                </div>
              ) : error ? (
                <div className="h-full flex flex-col items-center justify-center text-red-500 p-4 text-center">
                  <p className="mb-2">{error}</p>
                  <p className="text-sm text-gray-600">This section may not have been generated yet or may have expired. Try clicking the Refresh button.</p>
                </div>
              ) : (
                <iframe 
                  src={url || ''}
                  className="w-full h-full"
                  title={dialogTitle}                  
                  allowFullScreen
                />
              )}
            </div>
            
            <div className="mt-4 flex justify-end space-x-2">
              {/* <button
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                onClick={() => window.open(embedUrl, '_blank')}
                disabled={!embedUrl}
              >
                Open in New Tab
              </button> */}
              <Dialog.Close asChild>
                <button className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition-colors font-semibold">
                  Close
                </button>
              </Dialog.Close>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
});